import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Button, Flex, Text } from '@mantine/core';
import { Dropzone, DropzoneProps } from '@mantine/dropzone';
import { memo, useRef } from 'react';
import styled, { css } from 'styled-components';

export const ACCEPT_FILES_TYPES = {
	imagesAndPdf: {
		'image/jpeg': ['.jpeg'],
		'image/png': ['.png'],
		'application/pdf': ['.pdf'],
	},
	pdf: {
		'application/pdf': ['.pdf'],
	},
};

interface Props extends Partial<Omit<DropzoneProps, 'onDrop'>> {
	className?: string;
	onDropFile: (file: File) => void;
	size?: 's' | 'STD';
	title?: string;
	description?: string;
	disabled?: boolean;
	buttonText?: string;
	/** Test ID for E2E testing - applied to the file input element */
	dataTestId?: string;
}

const DropzoneV2 = ({
	className,
	onDropFile,
	title,
	description,
	buttonText,
	size,
	accept,
	disabled,
	dataTestId,
	...props
}: Props) => {
	const tCommon = useTranslationCommonUi();

	const openRef = useRef<() => void>(null);
	const isSTDSize = size === 'STD';

	return (
		<Dropzone
			onDrop={(files) => {
				files.forEach((file) => {
					onDropFile(file);
				});
			}}
			{...props}
			disabled={disabled}
			openRef={openRef}
			activateOnClick={false}
			className={className}
			accept={accept}
			data-testid={dataTestId}
		>
			<DashedBox>
				<Icon
					variant="upload-v2"
					size={isSTDSize ? 'xl' : 'lg'}
					color={defaultTheme.v2.color.secondary[800]}
				/>
				<Flex direction="column" gap="0.125rem">
					<Text fw={600}>{title ?? tCommon('dropzone.upload_or_drag')}</Text>
					{!!description && <Text>{description}</Text>}
					{!description && !!accept && isSTDSize && (
						<Text>
							{tCommon('dropzone.accept_files')} &nbsp;
							{Object.values(accept ?? {})
								.flat()
								.join(', ')}
						</Text>
					)}
				</Flex>
				<Button
					onClick={() => openRef?.current?.()}
					variant={isSTDSize ? 'primary' : 'secondary'}
					size="sm"
					leftSection={<Icon variant="files" />}
					sx={{ marginLeft: 'auto' }}
				>
					{buttonText ?? tCommon('dropzone.choose_files')}
				</Button>
			</DashedBox>
		</Dropzone>
	);
};

const DashedBox = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	border: 1px dashed ${defaultTheme.v2.color.neutral[600]};
	border-radius: 4px;
	padding: 1rem;
`;

const Memoized = memo(DropzoneV2);
const Styled = styled(Memoized)`
	padding: 0;
	border: none;
	cursor: auto;

	${({ disabled }) => {
		if (disabled) {
			return css`
				pointer-events: none;
				opacity: 0.32;
				&:hover {
					pointer-events: none;
				}
			`;
		}
		return css`
			&:hover {
				background-color: ${({ theme }) => theme.v2.color.secondary[200]};
			}
			.mantine-Dropzone-inner {
				pointer-events: all;
			}
		`;
	}}

	${({ size }) => {
		if (size === 'STD') {
			return css`
				${DashedBox} {
					flex-direction: column;
					align-items: flex-start;
					gap: 1rem;
					button {
						margin-left: unset;
					}
				}
			`;
		}
		return;
	}};
`;

export { Styled as DropzoneV2 };
